import { useDebounceFn } from 'ahooks';
import type { SearchableListProps } from '@archipro-design/aria';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import {
    Checkbox,
    SearchableList,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import * as S from './FilterSearchList.style';
import type {
    FilterStates,
    FilterStatesKey,
    SearchListKeys,
} from '../../type/filter-state';
import type { SearchAggregationParams, SearchIndex } from '../../type/api';
import { SEARCH_LIST_AGG_PARAM_KEY_MAP } from '../../config/search-filter';
import type {
    AggregateRequest,
    AggregateState,
} from '../../hook/use-aggregation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { compareSearchListLabels } from '../../util/compare-search-list-labels';

export interface FilterSearchListItem {
    label: string;
    value: string;
    checked?: boolean;
}

export interface SearchListFilter {
    kind: 'SearchList';
    filterKey: FilterStatesKey;
    /** The facet ID for the facet filters. */
    subFilterKey?: string;
    props: SearchableListProps<FilterSearchListItem> & { hideSearch?: boolean };
    // filter bar will display if it is true
    displayOnFilterBar?: boolean;
    filterBarOrder?: number;
    filterBarTitle?: string;
    displayOnMobileFilterBar?: boolean;
}

export interface SearchListFilterState extends Pick<SearchListFilter, 'kind'> {
    // this is listed items, with search query it might not contain the selected items
    items: FilterSearchListItem[];
    selectedItems?: FilterSearchListItem[];
    search?: string;
    after?: Record<string, unknown>;
    hasMoreItems?: boolean;
    rawSort?: boolean;
}

const DEFAULT_LOAD_SIZE = 40;

interface FilterSearchListProps {
    category?: SearchIndex;
    filterStates: FilterStates;
    filter: SearchListFilter;
    state: SearchListFilterState;
    updateFilter: (
        filterKey: FilterStatesKey,
        state: Partial<SearchListFilterState>,
        updateStates?: boolean,
        subFilterKey?: string,
    ) => void;
    aggregate: (request: AggregateRequest) => void;
    disabled?: boolean;
}

const FilterSearchList = ({
    filterStates,
    filter,
    state,
    updateFilter,
    aggregate,
    disabled,
}: FilterSearchListProps) => {
    const { props, filterKey, subFilterKey } = filter;
    const { hideSearch, ...restProps } = props;
    const { items, search, hasMoreItems, after, selectedItems, rawSort } =
        state;
    const [loading, setLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const isTyped = useRef(false);
    const { css } = useStyles({ emptyList: items.length === 0, hideSearch });
    const listRef = useRef<HTMLDivElement>(null);
    const { desktop } = useAppDisplayModeContext();

    const handleAggregateStateChange = (
        request: AggregateRequest,
        state: AggregateState,
        data?: FilterStates | undefined,
    ) => {
        if (state !== 'loaded') return;
        setLoading(false);
        const newFilterState = data?.[filterKey];
        let newListState: SearchListFilterState | null = null;
        if (Array.isArray(newFilterState)) {
            newFilterState.forEach((singleFilter) => {
                if (
                    typeof singleFilter !== 'number' &&
                    singleFilter.id === subFilterKey
                ) {
                    newListState = singleFilter.state;
                }
            });
        } else {
            if (newFilterState?.kind !== 'SearchList') return;
            newListState = newFilterState;
        }
        if (!newListState) return;

        let newUpdateState: Partial<SearchListFilterState>;
        let newItems: FilterSearchListItem[];
        let hasMoreItems = false;
        const returnItemCount = newListState?.items.length ?? 0;
        if (request.aggregateType === 'searchListLoadMore') {
            if (!newListState || newListState?.items.length === 0) {
                newItems = [...items];
            } else {
                const newComingItems = newListState.items.filter(
                    (item) => !items.find((i) => i.value === item.value),
                );
                newItems = [...items, ...newComingItems];
            }
        } else {
            if (newListState) {
                newItems = [...newListState.items];
            } else {
                newItems = [];
            }
        }
        hasMoreItems = returnItemCount !== 0;
        if (!rawSort) {
            newItems.sort((a, b) => compareSearchListLabels(a.label, b.label));
        }

        newUpdateState = {
            items: newItems,
            after: newListState?.after,
            hasMoreItems,
        };
        updateFilter(filterKey, newUpdateState, false, subFilterKey);
    };

    const loadMoreItems = async () => {
        if (!hasMoreItems || isTyping) return;
        setLoading(true);

        // if there's subFilterKey then it's a facet list, it does not support lazy loading
        if (subFilterKey) return;

        aggregate({
            aggregateType: 'searchListLoadMore',
            aggregateFilter: filterKey as SearchListKeys,
            filterStates,
            aggregationsParams: {
                [SEARCH_LIST_AGG_PARAM_KEY_MAP[filterKey as SearchListKeys]]: {
                    after,
                    size: DEFAULT_LOAD_SIZE,
                    search,
                },
            },
            onAggregateStateChange: handleAggregateStateChange,
        });
    };

    const searchAggregate = (search: string) => {
        setIsTyping(false);
        setLoading(true);
        let aggregationsParams: SearchAggregationParams;
        if (subFilterKey) {
            aggregationsParams = {
                aggFacetOptions: [{ facet: subFilterKey, search }],
            };
        } else {
            aggregationsParams = {
                [SEARCH_LIST_AGG_PARAM_KEY_MAP[filterKey as SearchListKeys]]: {
                    after,
                    size: DEFAULT_LOAD_SIZE,
                    search,
                },
            };
        }
        aggregate({
            aggregateType: 'searchListSearch',
            aggregateFilter: filterKey as SearchListKeys,
            filterStates,
            aggregationsParams,
            onAggregateStateChange: handleAggregateStateChange,
        });
    };

    const { run: searchAggregateRun } = useDebounceFn(searchAggregate, {
        wait: 500,
    });

    const scrollListToView = useCallback(() => {
        if (!listRef.current || desktop) return;
        listRef.current.scrollIntoView({
            block: 'center',
            behavior: 'smooth',
        });
    }, [desktop]);

    // only scroll into view when the user did search or list first mount
    useEffect(() => {
        scrollListToView();
    }, [scrollListToView]);

    useEffect(() => {
        if (!isTyped.current || loading) return;
        scrollListToView();
        isTyped.current = false;
    }, [loading, isTyping, scrollListToView]);

    return (
        <div className={css(S.FilterSearchList)} ref={listRef}>
            <SearchableList<FilterSearchListItem>
                {...restProps}
                items={items}
                inputValue={search}
                disabled={disabled}
                hasMoreItems={hasMoreItems}
                loadMoreItems={loadMoreItems}
                onSearchInputChange={(e, data) => {
                    setIsTyping(true);
                    isTyped.current = true;
                    updateFilter(
                        filterKey,
                        {
                            search: data?.value ?? '',
                            hasMoreItems: true,
                            items: [],
                            after: undefined,
                        },
                        false,
                        subFilterKey,
                    );
                    searchAggregateRun(data?.value ?? '');
                }}
                variables={{
                    optionListWidth: pxToRem(320),
                }}
                noResult={
                    !loading && items.length === 0 && !!search && !isTyping
                        ? `No result for “${search}”`
                        : null
                }
            >
                {({ index, data }) => {
                    if (!data) return null;
                    return (
                        <Checkbox
                            design={{ paddingBottom: pxToRem(8) }}
                            indicator={{ kind: 'checkbox' }}
                            disabled={disabled}
                            onChange={(_, checkData) => {
                                const checked = !!checkData?.checked;
                                const newList = [...items];
                                const item = newList[index];
                                if (item) {
                                    item.checked = checked;
                                }

                                const newSelectedItems = selectedItems
                                    ? [...selectedItems]
                                    : [];
                                const selectedIndex =
                                    newSelectedItems.findIndex(
                                        (item) => item.value === data.value,
                                    );
                                if (selectedIndex !== -1) {
                                    if (!checked) {
                                        newSelectedItems.splice(
                                            selectedIndex,
                                            1,
                                        );
                                    }
                                } else {
                                    if (checked) {
                                        newSelectedItems.push(data);
                                    }
                                }
                                updateFilter(
                                    filterKey,
                                    {
                                        kind: 'SearchList',
                                        items: newList,
                                        selectedItems: newSelectedItems,
                                    },
                                    true,
                                    subFilterKey,
                                );
                            }}
                            label={data.label}
                            checked={
                                !!selectedItems?.find(
                                    (selectedItem) =>
                                        selectedItem.value === data.value,
                                )
                            }
                        />
                    );
                }}
            </SearchableList>
        </div>
    );
};

export default FilterSearchList;
